import { useIsLoggedInValue } from '@portal/appLayer/auth/useIsLoggedInValue';
import { IS_V53_ENABLED_ENV } from '@portal/shared/constants/featureFlags';
import { CommonSectionProps } from '@portal/shared/studio/types';
import { useProductListStore } from '@portal/state/store/plpStore';
import { ShopV3Products } from '@portal/types/studio/studio';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import ShopListDefaultProducts from './ShopListDefaultProducts';
import ShopListMembersOnlyProducts from './ShopListMembersOnlyProducts';

const ShopListProducts = (sectionCmsData: ShopV3Products & CommonSectionProps) => {
  const isLoggedIn = useIsLoggedInValue();

  const { setHasMembersOnlySections } = useProductListStore(
    useShallow(({ actions }) => ({
      setHasMembersOnlySections: actions.pageRefs.setHasMembersOnlySections
    }))
  );

  const { useGlobalStateManagement, isDefaultProductSection, isMembersOnlyProductSection } =
    useMemo(() => sectionCmsData, [sectionCmsData]);

  // current condition for all 3 below to be true is for user to be logged in
  const showMembersOnlyProductSection = useMemo(
    () => IS_V53_ENABLED_ENV && isLoggedIn,
    [isLoggedIn]
  );

  const showFreeProducts = useMemo(() => IS_V53_ENABLED_ENV && isLoggedIn, [isLoggedIn]);
  const showMembersOnlyProducts = useMemo(() => IS_V53_ENABLED_ENV && isLoggedIn, [isLoggedIn]);

  useEffect(() => {
    // informs global store about the presence of members only sections coming from sanity
    // this is important for the calculation(s) of whether the page has loaded based on whether
    // members only sections have loaded
    if (
      showMembersOnlyProductSection &&
      !isDefaultProductSection &&
      isMembersOnlyProductSection &&
      useGlobalStateManagement
    ) {
      setHasMembersOnlySections(true);
    }
  }, [
    showMembersOnlyProductSection,
    isDefaultProductSection,
    isMembersOnlyProductSection,
    useGlobalStateManagement,
    setHasMembersOnlySections
  ]);

  // "All Products" default section
  if (isDefaultProductSection && !isMembersOnlyProductSection) {
    return (
      <ShopListDefaultProducts
        showFreeProducts={showFreeProducts}
        showMembersOnlyProducts={showMembersOnlyProducts}
        sectionCmsData={sectionCmsData}
      />
    );
  }

  // members-only section(s)
  if (showMembersOnlyProductSection && !isDefaultProductSection && isMembersOnlyProductSection) {
    return (
      <ShopListMembersOnlyProducts
        showFreeProducts={showFreeProducts}
        showMembersOnlyProducts={showMembersOnlyProducts}
        sectionCmsData={sectionCmsData}
      />
    );
  }

  // do not render anything if "Shop V3 Products" section does not meet either of the above 2 criteria
  return null;
};

export default ShopListProducts;
